// client-retention-software.js - Tier 3 Solution Page
// Medicare Client Retention Software

export const content = {
  heroSubtitle: "Purpose-built software for keeping Medicare clients. AI-powered risk scoring tells you who's about to leave. Automated workflows keep you connected. Real analytics show what's actually working.",

  problemSection: {
    title: "You Can't Protect What You Can't See",
    html: `
      <p>Here's what I learned the hard way: you can't protect your book of business with willpower and good intentions. You need visibility into what's actually happening with your clients—and most agents are flying blind.</p>

      <p>Think about it. Do you know which clients are at risk of leaving right now? Not a guess—actual data-driven insight? Do you know which of your plans have the worst retention? Do you know when your clients' coverage is about to change in ways that might make them shop around?</p>

      <p>If you're like most agents, the honest answer is no. You find out a client left when they don't renew, or when you call during AEP and discover they already enrolled elsewhere.</p>

      <p>That's not a retention strategy. That's hoping for the best while your book slowly erodes.</p>

      <p>The data tells the story: about 15-20% of Medicare clients churn annually. Nearly half of new enrollees leave within five years. And for agents without systems, those numbers are even worse.</p>

      <p>Client retention software exists to give you the visibility and tools you need to actually fight churn instead of just reacting to it.</p>
    `
  },

  features: [
    {
      icon: "target",
      title: "AI Risk Scoring",
      description: "Every client gets a churn risk score updated in real-time. Know who needs attention today—not after they've already left."
    },
    {
      icon: "bell",
      title: "Proactive Alerts",
      description: "Get notified when something changes: plan modifications, network disruptions, extended silence with a client. Stay ahead of problems."
    },
    {
      icon: "clock",
      title: "Automated Outreach",
      description: "Set up retention workflows that run automatically: birthday calls, quarterly check-ins, ANOC follow-ups, annual reviews. Never miss a touchpoint."
    },
    {
      icon: "chart",
      title: "Retention Analytics",
      description: "See your actual retention rates, not guesses. Track churn by plan, by segment, by time period. Understand patterns and fix problems."
    },
    {
      icon: "users",
      title: "Client Health Dashboard",
      description: "Visual overview of your entire book. Green, yellow, red status indicators. Prioritize your day based on who actually needs you."
    },
    {
      icon: "shield",
      title: "CMS Compliance Built-In",
      description: "All communications follow Medicare marketing rules. Scope tracking, proper documentation, compliant outreach templates."
    }
  ],

  featuresTitle: "What Retention Software Should Actually Do",

  mainContent: `
    <h2>The Problem With Generic CRMs</h2>

    <p>You might be thinking, "I already have a CRM." Fair enough. But let me ask you: was it built for retention?</p>

    <p>Most Medicare CRMs are sales tools that happen to store client information. They track leads, manage enrollments, and calculate commissions. That's all valuable—but it's not retention.</p>

    <p>Retention requires different capabilities:</p>

    <ul>
      <li><strong>Predictive analytics</strong> that identify at-risk clients before they start shopping</li>
      <li><strong>Plan intelligence</strong> that alerts you when changes affect your clients</li>
      <li><strong>Automated workflows</strong> that ensure consistent touchpoints year-round</li>
      <li><strong>Health scoring</strong> that prioritizes your time toward clients who need attention</li>
      <li><strong>Outcome tracking</strong> that shows what's actually working</li>
    </ul>

    <p>Your generic CRM probably doesn't do any of that. Which means you're managing retention manually, inconsistently, and ineffectively.</p>

    <h2>What Makes Pro IRP Different</h2>

    <h3>Built for Medicare, Built for Retention</h3>

    <p>Pro IRP isn't a generic CRM with Medicare features bolted on. It was designed from the ground up for one purpose: helping Medicare agents keep their clients.</p>

    <p>That means every feature, every screen, every workflow is oriented around retention. The dashboard doesn't show you lead counts—it shows you client risk scores. The alerts aren't about new enrollments—they're about clients who need attention. The analytics don't just track sales—they track who stayed and who left.</p>

    <h3>AI That Actually Helps</h3>

    <p>Our risk scoring uses machine learning trained on real Medicare churn patterns. It looks at:</p>

    <ul>
      <li>Contact recency (when did you last talk to this client?)</li>
      <li>Plan changes (are their benefits, premiums, or networks changing?)</li>
      <li>Service history (have they had problems that weren't resolved?)</li>
      <li>Client signals (are they asking questions that suggest shopping?)</li>
      <li>Market factors (is their plan losing Star ratings or market share?)</li>
    </ul>

    <p>The result is a score from 0-100 for every client, updated continuously. You know exactly who needs attention and why.</p>

    <h3>Workflows That Run Themselves</h3>

    <p>Retention isn't a one-time activity—it's ongoing. Pro IRP lets you set up automated workflows that ensure clients get touched at the right intervals:</p>

    <ul>
      <li>Birthday calls and messages (scheduled automatically)</li>
      <li>Quarterly check-ins (triggered automatically)</li>
      <li>ANOC follow-ups (timed to letter delivery)</li>
      <li>Annual review invitations (scheduled before AEP)</li>
      <li>Service follow-ups (triggered after issue resolution)</li>
    </ul>

    <p>You configure once, and the system handles the scheduling and reminders. No more "I meant to call them but forgot."</p>

    <h3>Real Retention Metrics</h3>

    <p>You can't improve what you don't measure. Pro IRP tracks:</p>

    <ul>
      <li><strong>Overall retention rate</strong> (how many clients stayed vs. left)</li>
      <li><strong>Retention by segment</strong> (MA vs. Med Supp, new vs. established)</li>
      <li><strong>Retention by plan</strong> (which plans keep clients vs. lose them)</li>
      <li><strong>Churn reasons</strong> (why clients who left actually left)</li>
      <li><strong>Touchpoint effectiveness</strong> (which outreach methods work best)</li>
    </ul>

    <p>This isn't vanity data—it's the information you need to actually improve retention over time.</p>

    <h2>Who This Is For</h2>

    <p>Pro IRP is designed for Medicare agents and agencies who:</p>

    <ul>
      <li>Have a book of business worth protecting (typically 100+ clients)</li>
      <li>Are tired of losing clients to competitors, especially during AEP</li>
      <li>Want systematic retention processes, not ad-hoc outreach</li>
      <li>Understand that keeping clients is more profitable than constantly replacing them</li>
    </ul>

    <p>If you're brand new with no book, start building first. But if you have clients to protect, this is built for you.</p>

    <h2>Getting Started</h2>

    <p>Most agents are up and running within a day:</p>

    <ol>
      <li>Import your client list (CSV upload or manual entry)</li>
      <li>Risk scores calculate automatically</li>
      <li>Set up your preferred workflows from templates</li>
      <li>Start working your highest-risk clients first</li>
    </ol>

    <p>There's no lengthy implementation or training period. The interface is designed to be intuitive for agents who already understand Medicare—we're not teaching you the business, we're giving you tools to do it better.</p>

    <h2>The ROI Case</h2>

    <p>Let's be concrete about the value:</p>

    <p>If you have 200 clients averaging $300/year in commission, and you're currently at 80% retention (industry average), you're losing 40 clients and $12,000 annually.</p>

    <p>If Pro IRP helps you improve to 90% retention—which is achievable with systematic approaches—you're losing only 20 clients and $6,000. That's $6,000 per year in commissions saved.</p>

    <p>The software pays for itself many times over if it prevents even a handful of clients from leaving.</p>
  `,

  faqs: [
    {
      question: "How does Pro IRP's risk scoring work?",
      answer: "Our AI analyzes multiple factors including contact recency, plan changes affecting the client, service issue history, and market indicators. Each client gets a score from 0-100 indicating their churn risk, updated in real-time. You see exactly who needs attention and why."
    },
    {
      question: "Can I import my existing client list?",
      answer: "Yes. Pro IRP accepts CSV imports with standard client fields—name, contact info, plan details, enrollment dates. Most agents complete their import within an hour, and risk scores begin calculating immediately."
    },
    {
      question: "Do I need to replace my existing CRM?",
      answer: "Pro IRP can serve as your primary client management system or complement existing tools. Many agents use carrier-provided systems for enrollment while using Pro IRP specifically for retention management and client outreach."
    },
    {
      question: "Is Pro IRP compliant with CMS marketing regulations?",
      answer: "Yes. Built with Medicare compliance in mind—communication templates follow CMS guidelines, scope of appointment tracking is built-in, and proper documentation is maintained. We stay updated on regulatory changes so you don't have to."
    },
    {
      question: "How long does it take to set up?",
      answer: "Most agents are operational within a day. Import your clients, configure your preferred workflows using pre-built templates, and start working. No lengthy implementation required."
    },
    {
      question: "What kind of support do you offer?",
      answer: "Email support, in-app help documentation, and video tutorials. We also offer onboarding calls for agencies implementing across multiple agents. We're agents too—we understand the questions you'll have."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage in 2024: Enrollment Update and Key Trends",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "Kaiser Family Foundation",
      accessedDate: "2024-12-10"
    },
    {
      title: "Turnover Among New Medicare Advantage Enrollees",
      url: "https://www.ajmc.com/view/turnover-among-new-medicare-advantage-enrollees-may-be-greater-than-perceived",
      publisher: "AJMC",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
